import {Injectable} from '@angular/core';
import {ResourceListFilter, ResourceListQuery, ResourceQuery} from './models/resource.query';
import {instanceToInstance, plainToInstance} from 'class-transformer';
import {ResourceModel} from './models/resource.model';
import {
  getBase64,
  IdCodeNameModel,
  GLOBAL,
  MesApiService,
  FilterModel,
  PagedData,
  Page,
  getIdsInArrayWrapper
} from 'common-shared';
import {forkJoin, Observable, concatMap, map, switchMap, of} from "rxjs";
import {ResourceLogModel} from "./models/resource-log.model";
import {ResourcesQuery} from "./resource.store";
import {marker} from "@jsverse/transloco-keys-manager/marker";
import {PropertyLineModel, SpecLineModel, UserService, WithSpecsModel} from 'web-shared';

@Injectable({
  providedIn: 'root'
})
export class ResourceService extends MesApiService {

  constructor(public query: ResourcesQuery, private userService: UserService) {
    super('resource', 'manufacturing');
  }

  getAll$ = this.query.selectAll();

  loadOne(id: string): Observable<ResourceModel> {
    return this.getSingle<ResourceModel>({
      id: id
    }).pipe(map(value => {
      return plainToInstance(ResourceModel, value.data);
    }));
  }

  addNew(resource: ResourceModel): Observable<ResourceModel> {
    return resource.KindClv === GLOBAL.Classificators.ResourceKind.Person ? this.addWithUser(resource) : this.addResource(resource);
  }

  private addResource(resource: ResourceModel): Observable<ResourceModel> {
    return this.add<ResourceModel>(resource).pipe(map(value => {
      return plainToInstance(ResourceModel, value.data);
    }));
  }

  private addWithUser(resource: ResourceModel): Observable<ResourceModel> {
    const addOrSaveUser$ = resource.UserId ? this.userService.save(resource.User) : this.userService.addNew(resource.User);
    return addOrSaveUser$.pipe(concatMap(user => {
      resource.UserId = user.Id;
      resource.User = user;
      return this.addResource(resource);
    }));
  }

  save(resource: ResourceModel): Observable<ResourceModel> {
    return ([GLOBAL.Classificators.ResourceKind.Person, GLOBAL.Classificators.ResourceKind.Area].includes(resource.KindClv) && resource.User?.Email != null) ? this.saveWithUser(resource) : this.saveResource(resource);
  }

  private saveResource(resource: ResourceModel): Observable<ResourceModel> {
    return this.update<ResourceModel>(resource.Id, resource).pipe(map(value => {
      return plainToInstance(ResourceModel, value.data);
    }));
  }

  private saveWithUser(resource: ResourceModel): Observable<ResourceModel> {
    return this.userService.save(resource.User).pipe(concatMap(value => {
      resource.UserId = value.Id;
      resource.User = value;
      return this.saveResource(resource)
    }));
  }

  remove(resource: ResourceModel) {
    return resource.KindClv === GLOBAL.Classificators.ResourceKind.Person ? this.removeWithUser(resource) : this.removeResource(resource);
  }

  private removeResource(resource: ResourceModel): Observable<string> {
    return this.delete<string>(resource.Id).pipe(map(value => {
      return value.data;
    }));
  }

  private removeWithUser(resource: ResourceModel): Observable<string> {
    return forkJoin({
      UserId: this.userService.remove(resource.User),
      ResourceId: this.removeResource(resource)
    }).pipe(map(value => {
      return value.ResourceId;
    }));
  }

  downloadExcel(filter: FilterModel<ResourceListFilter>) {
    return this.http.post(this.getRoute({action: 'ExportToExcel'}), filter, {
      observe: 'response',
      responseType: 'blob'
    }).pipe(map(value => {
      return this.getFileNameAndData(value);
    }));
  }

  uploadExcel(file: File): Observable<number> {
    return getBase64(file).pipe(switchMap(fileData => {
      return this.addGet<string, number>(
        fileData,
        {action: 'ImportFromExcel'}
      ).pipe(map(value => {
        return value.data;
      }));
    }));
  }

  editPropertyLine(propertyLineModel: PropertyLineModel) {
    return this.update(propertyLineModel.Id, propertyLineModel, {action: 'EditPropertyLine'});
  }

  updateCurrentUserPropertyLineValue(propertyId: string, value: string) {
    const specLineModel = new SpecLineModel({PropertyId: propertyId, Value: value});
    return this.update(specLineModel.Id, specLineModel, {action: 'UpdateCurrentUserPropertyLineValue'}).pipe(map(value => {
      return value.data;
    }));
  }

  addCurrentUserListPropertyLineValue(propertyId: string, value: string, name: string = null) {
    const specLineModel = new SpecLineModel({PropertyId: propertyId, Value: value});
    name = name === null ? name : '?name=' + name;
    return this.update(name, specLineModel, {action: 'AddCurrentUserListPropertyLineValue'}).pipe(map(value => {
      return value.data;
    }));
  }

  getCurrentUserSpecs(): Observable<SpecLineModel[]> {
    return this.getList<SpecLineModel>({action: 'GetCurrentUserSpecs'}).pipe(map(value => {
      return plainToInstance(SpecLineModel, value.data);
    }));
  }

  getLogs(id: string): Observable<ResourceLogModel[]> {
    return this.getList<ResourceLogModel>({
      action: 'GetLogs'
    }, {id: id}).pipe(map(value => {
      return plainToInstance(ResourceLogModel, value.data);
    }));
  }

  addLog(log: ResourceLogModel, logs: ResourceLogModel[]): Observable<ResourceLogModel> {
    return this.addGet<{}, ResourceLogModel>({
      template: log,
      logs: logs
    }, {action: 'AddLog'}).pipe(map(value => {
      return plainToInstance(ResourceLogModel, value.data);
    }));
  }

  addLogs(logs: ResourceLogModel[]): Observable<ResourceLogModel> {
    return this.addGet<{}, ResourceLogModel>({
      items: logs
    }, {action: 'AddLogs'}).pipe(map(value => {
      return plainToInstance(ResourceLogModel, value.data);
    }));
  }

  saveLog(log: ResourceLogModel): Observable<ResourceLogModel> {
    return this.update<ResourceLogModel>(log.Id, log, {action: 'EditLog'}).pipe(map(value => {
      return plainToInstance(ResourceLogModel, value.data);
    }));
  }

  removeLog(log: ResourceLogModel): Observable<ResourceLogModel> {
    return this.delete<ResourceLogModel>(log.Id, {action: 'DeleteLog'}).pipe(map(value => {
      return plainToInstance(ResourceLogModel, value.data);
    }));
  }

  getStatusClassificator(resourceId: string) {
    return this.getSingle<string>({action: 'GetStatusClassificator'}, {id: resourceId}).pipe(map(value => {
      return value.data;
    }));
  }

  updateSpecLine(resourceId: string, specLine: SpecLineModel) {
    return this.updateGet<ResourceModel, SpecLineModel>(resourceId, specLine, {action: 'UpdateSpecLine'}).pipe(map(value => {
      return plainToInstance(SpecLineModel, value.data);
    }));
  }

  copy(resourceId: string, newCode: string, newName: string, replaceId: string) {
    return this.updateGet<IdCodeNameModel, string>(resourceId, {
      Code: newCode, Name: newName, Id: replaceId
    }, {action: 'Copy'}).pipe(map(value => {
      return value.data;
    }));
  }

  addByTemplateId(resourceId: string, newCode: string, newName: string) {
    return this.updateGet<IdCodeNameModel, string>(resourceId, {
      Code: newCode, Name: newName
    }, {action: 'AddByTemplateId'}).pipe(map(value => {
      return value.data;
    }));
  }

  getResourceIdByUserId(userId: string) {
    return this.getSingle<string>({
      id: userId,
      action: 'GetResourceIdByUserId'
    }).pipe(map(value => {
      return value.data;
    }));
  }

  getByFilter(filter: FilterModel<ResourceListFilter>) {
    let clonedFilter = instanceToInstance(filter);
    return this.addGet<FilterModel<ResourceListFilter>, PagedData<ResourceListQuery>>(clonedFilter, {action: 'GetByFilter'}).pipe(map(value => {
      return new PagedData<ResourceListQuery>(plainToInstance(ResourceListQuery, value.data.Data), plainToInstance(Page, value.data.Page), value.data.DistinctSpecs);
    }));
  }

  getWithSpecs(resourceIds: string[], propertyIds: string[]) {
    return this.addGet<any, WithSpecsModel[]>({
      resourceIds: resourceIds.filter(id => id),
      propertyIds: propertyIds.filter(id => id)
    }, {action: 'GetWithSpecs'}).pipe(map(value => {
      return plainToInstance(WithSpecsModel, value.data);
    }));
  }

  getWithToResourceConnections(resourceIds: string[]): Observable<ResourceQuery[]> {
    if (resourceIds.length == 0)
      return of([]);
    return this.addGet<{ items: string[] }, ResourceQuery[]>(getIdsInArrayWrapper(resourceIds), {
      action: 'GetWithToResourceConnections'
    }).pipe(map(value => {
      return plainToInstance(ResourceQuery, value.data);
    }));
  }

  getToConnectionTitle(resource: ResourceModel) {
    switch (resource?.KindClv) {
      case GLOBAL.Classificators.ResourceKind.Machine:
      case GLOBAL.Classificators.ResourceKind.Template:
        return marker('ime.manufacturing.resources.Parent Groups and Areas');
      case GLOBAL.Classificators.ResourceKind.Person:
        return marker('ime.manufacturing.resources.Parent Groups and Resources');
      case GLOBAL.Classificators.ResourceKind.Skill:
        return marker('ime.manufacturing.resources.Pricelists and Persons');
      case GLOBAL.Classificators.ResourceKind.Area:
        return marker('ime.manufacturing.resources.Parent Groups and Resources');
      case GLOBAL.Classificators.ResourceKind.Workcenter:
        return marker('ime.manufacturing.resources.Parent Groups and Resources');
      default:
        return marker('ime.manufacturing.resources.Parent Groups and Resources');
    }
  }

  getFromConnectionTitle(resource: ResourceModel) {
    switch (resource?.KindClv) {
      case GLOBAL.Classificators.ResourceKind.Machine:
      case GLOBAL.Classificators.ResourceKind.Template:
        return marker('ime.manufacturing.resources.Members');
      case GLOBAL.Classificators.ResourceKind.Person:
        return marker('ime.manufacturing.resources.Skills and Machines');
      case GLOBAL.Classificators.ResourceKind.Skill:
        return marker('ime.manufacturing.resources.Machines in area');
      case GLOBAL.Classificators.ResourceKind.Area:
        return marker('ime.manufacturing.resources.Machines in area');
      case GLOBAL.Classificators.ResourceKind.Workcenter:
        return marker('ime.manufacturing.resources.Members');
      default:
        return marker('ime.manufacturing.resources.Parent Groups and Resources');
    }
  }
}
